/**
 * `useUploadProjectFile` mutation hook.
 *
 * Orchestrates the 3-step upload chain (request signed URL → POST file to
 * S3 → confirm with the API) as a single TanStack mutation. The
 * hook accepts an optional `folderId` so callers can target a specific
 * folder (the ProjectDetail folder upload) or omit it (the create-project
 * wizard upload, which posts to the project root).
 *
 * Cache invalidation: targets the folder's files key when folderId is
 * provided AND the broader folders prefix so file counts in the parent
 * folder listing also refresh. When no folderId is provided (create
 * wizard) the hook does NOT invalidate folder caches — the wizard is
 * pre-publish (the project itself isn't even browsable yet) so there is
 * nothing to refresh.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PROJECTS_QUERY_KEYS } from '@/entities/project';
import { track } from '@/shared/analytics';
import { confirmProjectFileUpload, requestProjectFileUpload } from '@/entities/project-file';
import { uploadToS3 } from '@/shared/api/upload-to-s3';
import { sanitizeErrorMessage } from '@/lib/upload-queue/error-message';
import { isUserAbort } from '@/lib/upload-queue/is-user-abort';
import { enqueueUpload } from '@/lib/upload-queue/upload-queue-bindings';
import { logger } from '@/lib/utils/logger';

interface UseUploadProjectFileOptions {
  projectId: string;
  /**
   * Optional — when provided, the file is uploaded into the folder and
   * folder caches are invalidated. When omitted (create-project wizard)
   * the file is uploaded to the project root and no folder caches are
   * invalidated (nothing to refresh pre-publish).
   */
  folderId?: string;
}

export function useUploadProjectFile(options: UseUploadProjectFileOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  // Shared by onSuccess + onError. Closure over
  // queryClient/projectId/folderId — no React state, safe to define inside
  // the hook body without useCallback.
  const invalidateFolderCaches = (): void => {
    if (!folderId) return;

    void queryClient.invalidateQueries({
      queryKey: PROJECTS_QUERY_KEYS.folders.files(projectId, folderId),
    });
    void queryClient.invalidateQueries({
      queryKey: PROJECTS_QUERY_KEYS.folders.detail(projectId, folderId),
    });
    // Bust ALL folder list queries (root + nested) so file counts refresh
    // in any open table.
    void queryClient.invalidateQueries({
      queryKey: PROJECTS_QUERY_KEYS.folders.list(projectId).slice(0, 3),
    });
  };

  return useMutation({
    mutationFn: async (file: File) => {
      // Feed the global upload-queue. The queue owns the
      // AbortSignal so a row-level cancel from the sidebar tray aborts the
      // S3 POST mid-flight. Cross-feature value edge is inverted via the
      // `lib/upload-queue` binding seam — the wire never imports from
      // `@/features/upload-queue` directly.
      const handle = enqueueUpload({ file, source: 'folder', projectId, folderId });

      try {
        const uploadResponse = await requestProjectFileUpload(projectId, {
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type,
          folderId,
        });

        await uploadToS3(
          uploadResponse.uploadUrl,
          uploadResponse.uploadFields,
          file,
          handle.abortSignal,
          handle.onProgress,
        );

        await confirmProjectFileUpload(projectId, uploadResponse.fileId, uploadResponse.versionId);

        handle.markSuccess();
        return uploadResponse;
      } catch (err) {
        // User-cancel race: queue.cancel() already aborted the signal and
        // transitioned the item to 'cancelled'; axios then rejects with a
        // CanceledError. Skip the markFailed path so we don't churn the
        // tray row and so onError consumers can distinguish cancel from
        // failure via the propagated error type.
        if (isUserAbort(err)) {
          throw err;
        }
        // eslint-disable-next-line i18next/no-literal-string -- pre-sanitize placeholder; `sanitizeErrorMessage` returns the translated fallback
        const raw = err instanceof Error ? err.message : 'Upload failed';
        logger.error('[upload-project-file] upload failed', { file: file.name, raw, err });
        handle.markFailed(sanitizeErrorMessage(raw));
        throw err; // preserve mutation onError contract
      }
    },
    onSuccess: () => {
      track('project_file_uploaded', { project_id: options.projectId });
      invalidateFolderCaches();
    },
    // Cache invalidation must not be asymmetric.
    // The 3-step chain can succeed at S3 and then fail at `confirm` —
    // the S3 object exists, the server-side DB row was never finalized,
    // and the folder listing was stale. Without an onError invalidation
    // the cached listing diverged from server truth until the next
    // navigation or refresh. Invalidate the same folder keys here so
    // the user always sees what the server actually has.
    //
    // The invalidation is gated on isUserAbort. The
    // mutationFn re-throws CanceledError on user cancel — onError would
    // fire and bust 3 query keys for no reason (server state is
    // unchanged when the user aborts before/during the chain).
    onError: (err) => {
      if (isUserAbort(err)) return;
      invalidateFolderCaches();
    },
  });
}
